import type { NonUndefined, ValuesType } from 'utility-types'
import React, { type FC, type PropsWithChildren, type ReactNode, useMemo } from 'react'
import { type FormatListOptions, FormattedList, FormattedMessage, FormattedRelativeTime, IntlProvider, type IntlShape, useIntl } from 'react-intl'
import type { Props as ReactIntlTranslationProps } from 'react-intl/src/components/message'

import type { ReadonlyDate, ReadonlyRecord } from '../../../utils'

import { Translation } from '../../../domain'

export type TranslationProviderProps = Readonly<{
    internationalization: Pick<IntlShape, 'locale' | 'messages' | 'onError'>
}>

export const TranslationProvider: FC<PropsWithChildren<TranslationProviderProps>> = ({ internationalization, children }) => {
    const { locale, messages, onError } = internationalization

    return (
        <IntlProvider locale={locale} messages={messages} onError={onError}>
            {children}
        </IntlProvider>
    )
}

/**
 * There is type mismatch between translation values that 'react-intl' expects (ReactIntlTranslationValues)
 * and the values we use across the project (CybusTranslationValues).
 * Currently, the only difference is Date vs. ReadonlyDate.
 *
 * The types below shall not be exported,
 * all type assertions of kind `cybusTranslationValues as ReactIntlTranslationValues` should be made in this file.
 */
type ReactIntlTranslationValues = Readonly<ReactIntlTranslationProps['values']>
type CybusTranslationValues = ReadonlyRecord<string, Exclude<ValuesType<NonUndefined<ReactIntlTranslationValues>>, Date> | ReadonlyDate>

export type FormatTranslationProps = Readonly<{
    /**
     * The targeted translation
     */
    id: Translation
    /**
     * The values to the translation
     */
    values?: CybusTranslationValues
}>

type TranslatorHook = {
    formatTranslation(id: FormatTranslationProps['id'], values?: FormatTranslationProps['values']): string
    formatList(values: readonly ReactNode[], options?: FormatListOptions): string
}

export const useTranslator = (): TranslatorHook => {
    const intl = useIntl()

    return useMemo(
        () => ({
            formatTranslation: (id, values) => intl.formatMessage({ id }, values as ReactIntlTranslationValues) as string,
            formatList: (values, options) => intl.formatList(values, options) as string,
        }),
        [intl]
    )
}

export const FormattedTranslation: FC<FormatTranslationProps> = ({ id, values }: FormatTranslationProps) => (
    <FormattedMessage id={id} values={values as ReactIntlTranslationValues} />
)

export const FormattedTimePast: FC<Readonly<{ value: ReadonlyDate }>> = ({ value }) => (
    <FormattedRelativeTime unit="second" value={(value.getTime() - Date.now()) / 1_000} updateIntervalInSeconds={1} />
)

/**
 * Use this component to render dates
 * Formatting options are:
 *  - Datetime: both date and time are displayed
 *  - Time Only: only time is displayed
 *  - Date Only: only date is displayed
 */
export const FormattedDateTime: FC<Readonly<{ date: ReadonlyDate, format: 'datetime' | 'dateOnly' | 'timeOnly' }>> = ({ format, date }) => (
    <FormattedTranslation id={Translation.DATETIME} values={{ type: format, date }} />
)

export { FormattedList }
